import * as rrd from 'react-router-dom';
import { useEffect, useState } from 'react';

import {
  isSingleConversion,
  isAccountConversion,
  isProfileConversion,
  isSelectionConversion,
} from '@/utils';
import {
  useAuth,
  useOAuth,
  useMounted,
  useAuthRefresh,
  useRetryConversion,
  useConversionDetails,
} from '@/hooks';
import {
  Alert,
  URLInput,
  OAuthForm,
  SupportCard,
  ConvertButton,
  DetailsToggle,
  ErrorRenderer,
  PrivacyToggle,
  PlaylistPicker,
  PlaylistRenderer,
  ConversionRenderer,
} from '@/components';
import { PLATFORM_CAPTIONS } from '@/constants';

const { Link, useParams, useLocation } = rrd.default ?? rrd;

const RetryConverter = () => {
  const oauth = useOAuth();

  const mounted = useMounted();

  const { state } = useLocation();

  const { isAuthorized, onUnauthorized } = useAuth();

  const {
    getDetails,
    data: details,
    error: detailsError,
    isLoading: detailsLoading,
  } = useConversionDetails({ onUnauthorized });

  const { conversionId: retryConversionId } = useParams();

  // Prefer location state, fall back to server-fetched details
  const ctx = state || details || {};

  const {
    type,
    source,
    target,
    isTargetPrivate,
    sourceAccountId: originalSourceAccountId,
    targetAccountId: originalTargetAccountId,
  } = ctx;

  const isPrivate = isTargetPrivate ?? true;

  const {
    error,
    progress,
    conversion,
    isComplete,
    forceRetry,
    handleRetry,
    isConverting,
    alreadyRetried,
  } = useRetryConversion({
    type,
    source,
    target,
    onUnauthorized,
    retryConversionId,
  });

  const sourceAuth = oauth.source[source];
  const targetAuth = oauth.target[target];

  const isSingle = isSingleConversion(type);
  const isProfile = isProfileConversion(type);
  const isSelection = isSelectionConversion(type);
  const needsSourceAuth = isAccountConversion(type);

  useEffect(() => {
    if (retryConversionId) {
      getDetails(retryConversionId);
    }
  }, [retryConversionId]);

  useAuthRefresh(target, targetAuth, isAuthorized);
  useAuthRefresh(source, needsSourceAuth ? sourceAuth : null, isAuthorized);

  useEffect(() => {
    if (!targetAuth || !originalTargetAccountId) return;

    if (targetAuth.accountId !== originalTargetAccountId) {
      const match = targetAuth.accounts?.find(
        (a) => a.accountId === originalTargetAccountId
      );

      if (match) {
        targetAuth.switchAccount(match);
      }
    }
  }, [
    target,
    targetAuth?.accounts,
    targetAuth?.accountId,
    originalTargetAccountId,
  ]);

  useEffect(() => {
    if (!needsSourceAuth || !sourceAuth || !originalSourceAccountId) return;

    if (sourceAuth.accountId !== originalSourceAccountId) {
      const match = sourceAuth.accounts?.find(
        (a) => a.accountId === originalSourceAccountId
      );

      if (match) {
        sourceAuth.switchAccount(match);
      }
    }
  }, [
    source,
    needsSourceAuth,
    sourceAuth?.accounts,
    sourceAuth?.accountId,
    originalSourceAccountId,
  ]);

  // Treat as ready if the correct account is in the list (switchAccount will
  // run imminently to swap it in).
  const hasTargetAccount =
    targetAuth?.accounts?.some(
      (a) => a.accountId === originalTargetAccountId
    ) ?? false;

  const isTargetReady =
    targetAuth?.isSignedIn &&
    (!originalTargetAccountId ||
      targetAuth.accountId === originalTargetAccountId ||
      hasTargetAccount);

  const hasSourceAccount =
    sourceAuth?.accounts?.some(
      (a) => a.accountId === originalSourceAccountId
    ) ?? false;

  const isSourceReady =
    !needsSourceAuth ||
    (sourceAuth?.isSignedIn &&
      (!originalSourceAccountId ||
        sourceAuth.accountId === originalSourceAccountId ||
        hasSourceAccount));

  const isAuthReady = isTargetReady && isSourceReady;

  const selectionPlaylists =
    details?.playlists
      ?.filter(({ status }) => status === 'failure')
      .map(({ id, name = 'Untitled Playlist' }) => ({ id, name })) || null;

  const selectionIds = selectionPlaylists?.map(({ id }) => id) || [];

  const [isDetailsOpen, setIsDetailsOpen] = useState(true);

  const oAuthError = sourceAuth?.error || targetAuth?.error;

  if (!mounted) return null;

  if (!retryConversionId) {
    return (
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <Alert type="warning">
            No conversion to retry. <Link to="/history">Go to History</Link>
          </Alert>
        </div>
      </div>
    );
  }

  if (!type && detailsLoading) {
    return null;
  }

  if (!type && !detailsLoading && detailsError) {
    return (
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <Alert type="danger">{detailsError}</Alert>
        </div>
      </div>
    );
  }

  if (!type && !detailsLoading) {
    return (
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <Alert type="warning">
            Conversion not found. <Link to="/history">Go to History</Link>
          </Alert>
        </div>
      </div>
    );
  }

  const sourceUrl = details?.sourceUrl || '';

  const sourceProfileUrl = details?.sourceProfileUrl || '';

  const isFrozen = isConverting || isComplete;

  const showDetailsBody = !isComplete || isDetailsOpen;

  return (
    <div className="row justify-content-center">
      <div className="col-lg-8">
        <DetailsToggle
          isComplete={isComplete}
          isConverting={isConverting}
          isDetailsOpen={isDetailsOpen}
          setIsDetailsOpen={setIsDetailsOpen}
        />

        {showDetailsBody && (
          <div className={isComplete ? 'mt-3' : 'mb-3'}>
            {isSingle && sourceUrl && (
              <URLInput
                disabled
                url={sourceUrl}
                source={source}
                caption="playlist"
              />
            )}

            {isProfile && sourceProfileUrl && (
              <URLInput
                disabled
                source={source}
                caption="profile"
                url={sourceProfileUrl}
              />
            )}

            {needsSourceAuth && (
              <div className="oauth-group">
                <OAuthForm
                  role="source"
                  {...sourceAuth}
                  platform={source}
                  disabled={isFrozen}
                />
              </div>
            )}

            <div className="oauth-group">
              <OAuthForm
                {...targetAuth}
                platform={target}
                disabled={isFrozen}
                role={needsSourceAuth ? 'target' : undefined}
              />
            </div>

            {isSelection && (
              <div className="mt-3">
                <PlaylistPicker
                  disabled
                  target={target}
                  isComplete={isComplete}
                  isLoading={detailsLoading}
                  selectedIds={selectionIds}
                  isConverting={isConverting}
                  label="Playlists to transfer"
                  playlists={selectionPlaylists}
                />
              </div>
            )}

            <div className="privacy-convert-group mt-3">
              <PrivacyToggle disabled type={type} isPrivate={isPrivate} />

              <ConvertButton
                disabled={!isAuthReady || isFrozen}
                label={alreadyRetried ? 'Retry Anyway' : 'Retry'}
                onConvert={alreadyRetried ? forceRetry : handleRetry}
              />
            </div>

            {oAuthError ? (
              <Alert type="danger">{oAuthError}</Alert>
            ) : (
              !isAuthReady &&
              !isFrozen &&
              (needsSourceAuth ? (
                <>
                  {sourceAuth?.isSignedIn &&
                    !isSourceReady &&
                    originalSourceAccountId && (
                      <Alert type="warning">
                        Connect the {source} account used in the original
                        conversion to retry!
                      </Alert>
                    )}

                  {targetAuth?.isSignedIn &&
                    !isTargetReady &&
                    originalTargetAccountId && (
                      <Alert type="warning">
                        Connect the {target} account used in the original
                        conversion to retry!
                      </Alert>
                    )}

                  {(!sourceAuth?.isSignedIn || !targetAuth?.isSignedIn) &&
                    !sourceAuth?.isSigningIn &&
                    !targetAuth?.isSigningIn && (
                      <Alert type="info">
                        Connect to allow us to transfer your{' '}
                        {PLATFORM_CAPTIONS[source]} playlists to{' '}
                        {PLATFORM_CAPTIONS[target]}.
                      </Alert>
                    )}
                </>
              ) : (
                <>
                  {targetAuth?.isSignedIn &&
                    !isTargetReady &&
                    originalTargetAccountId && (
                      <Alert type="warning">
                        Connect the {target} account used in the original
                        conversion to retry!
                      </Alert>
                    )}

                  {!targetAuth?.isSignedIn && !targetAuth?.isSigningIn && (
                    <Alert type="info">
                      Connect to {PLATFORM_CAPTIONS[target]} to allow us to
                      create the playlist on your account.
                    </Alert>
                  )}
                </>
              ))
            )}
          </div>
        )}

        {(isConverting || isComplete) &&
          (isSingle ? (
            <PlaylistRenderer
              {...conversion}
              source={source}
              target={target}
              progress={progress}
              isConverting={isConverting}
            />
          ) : (
            <ConversionRenderer
              {...conversion}
              source={source}
              target={target}
              progress={progress}
              isConverting={isConverting}
            />
          ))}

        {error && <ErrorRenderer error={error} />}

        {alreadyRetried && (
          <Alert type="warning">
            This conversion was already retried successfully. Are you sure you
            want to retry again?
          </Alert>
        )}

        {isComplete && (
          <>
            <SupportCard />

            <div className="text-center mt-3">
              <Link
                to="/history"
                className="btn btn-outline-custom btn-outline-accent"
              >
                Back to History
              </Link>
            </div>
          </>
        )}

        {!isConverting && !isComplete && (error || alreadyRetried) && (
          <div className="text-center mt-3">
            <Link
              to="/history"
              className="btn btn-outline-custom btn-outline-accent"
            >
              Back to History
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetryConverter;
